import React from 'react';
import { shortDayLabel } from '../../utils/appDate';
import { T, NUMERIC, money } from '../customer/customers.theme';
import { EditIcon, ListIcon, PhoneIcon, Spinner, TrashIcon } from '../customer/CustomersUI';
import { COPY, vendorBalanceColor, vendorBalanceLabel } from './vendors.copy';

/**
 * One supplier's book.
 *
 * The customer ledger's twin: oldest first, the running balance reading
 * downward, newest line at the bottom and scrolled to on open.
 *
 * Two things differ, and both come from the direction of the debt. The balance
 * carries a word beside it — Owing, Settled, In credit — because "$240" on a
 * supplier is ambiguous in a way it is not on a customer. And there is no
 * Statement button: a statement is something you send to someone who owes YOU.
 *
 * The lines an invoiced expense wrote appear here with no help from this
 * screen: they are vendor transactions like any other.
 */

// The last track holds the two row actions. They are always drawn, never
// revealed on hover — a control you have to hunt for is one people stop
// believing exists.
const COLUMNS = 'minmax(0, 1fr) 150px 130px 130px 74px';

const headCell = {
  fontSize: 11.5,
  fontWeight: 700,
  letterSpacing: 0.3,
  color: T.inkSoft,
};

function RowAction({ label, onClick, tone, children }) {
  const [hovered, setHovered] = React.useState(false);
  const danger = tone === 'danger';

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        background: hovered ? (danger ? T.dangerTint : T.hover) : 'transparent',
        border: 'none',
        borderRadius: T.radiusControl,
        color: hovered ? (danger ? T.danger : T.ink) : T.inkGhost,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function LedgerRow({ transaction, currency, last, onEdit, onDelete }) {
  const isCharge = Number(transaction.debit || 0) > 0;
  const amount = isCharge ? transaction.debit : transaction.credit;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: COLUMNS,
        gap: 16,
        alignItems: 'center',
        padding: '13px 20px',
        background: last ? T.sunk : 'transparent',
        borderBottom: last ? 'none' : `1px solid ${T.hairline}`,
      }}
    >
      <span
        title={transaction.description}
        style={{
          fontSize: 13,
          color: T.ink,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {transaction.description || '—'}
      </span>

      <span style={{ fontSize: 13, color: T.inkSoft, ...NUMERIC }}>
        {shortDayLabel(transaction.date)}
      </span>

      {/* A new bill and a payment are the same number in the same column, so
          colour is the only thing telling them apart. */}
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: isCharge ? T.brand : T.paid,
          textAlign: 'right',
          ...NUMERIC,
        }}
      >
        {money(amount, currency)}
      </span>

      <span
        style={{
          fontSize: 13,
          fontWeight: last ? 700 : 400,
          color: last ? T.ink : T.inkSoft,
          textAlign: 'right',
          ...NUMERIC,
        }}
      >
        {money(transaction.balance, currency)}
      </span>

      <span style={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <RowAction label={COPY.edit} onClick={() => onEdit(transaction)}>
          <EditIcon size={14} />
        </RowAction>
        <RowAction label={COPY.delete} tone="danger" onClick={() => onDelete(transaction)}>
          <TrashIcon size={14} />
        </RowAction>
      </span>
    </div>
  );
}

export default function VendorLedger({
  vendor,
  transactions,
  loading,
  currency,
  onPayment,
  onCharge,
  onEdit,
  onEditTransaction,
  onDeleteTransaction,
}) {
  const bottomRef = React.useRef(null);

  // Jump to the newest line whenever the vendor or the ledger changes.
  React.useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollTop = bottomRef.current.scrollHeight;
  }, [vendor && vendor._id, transactions.length]);

  if (!vendor) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          background: T.canvas,
          textAlign: 'center',
        }}
      >
        <span style={{ display: 'flex', color: T.inkGhost }}>
          <ListIcon />
        </span>
        <span style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>{COPY.pickTitle}</span>
        <span style={{ fontSize: 13, color: T.inkSoft, lineHeight: 1.5, maxWidth: 260 }}>
          {COPY.pickBody}
        </span>
      </div>
    );
  }

  const meta = [vendor.phone, vendor.agent, vendor.city].filter(Boolean).join(' · ');

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        background: T.canvas,
        boxSizing: 'border-box',
      }}
    >
      {/* ── who, what is owed, and the two things you can do about it ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24,
          padding: '22px 28px',
          background: T.surface,
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: -0.2, color: T.ink }}>
              {vendor.name}
            </span>
            <button
              type="button"
              onClick={() => onEdit(vendor)}
              title={COPY.formEdit}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 30,
                height: 30,
                background: 'transparent',
                border: 'none',
                borderRadius: T.radiusControl,
                color: T.inkFaint,
                cursor: 'pointer',
              }}
            >
              <EditIcon />
            </button>
          </div>
          <span
            style={{
              fontSize: 13.5,
              color: T.inkSoft,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {meta || '—'}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 26, flexShrink: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
            {/* The word matters here in a way it does not on a customer: a bare
                figure against a supplier does not say which way it points. */}
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: T.inkFaint }}>
              {vendorBalanceLabel(vendor.balance).toUpperCase()}
            </span>
            <span
              style={{
                fontSize: 26,
                fontWeight: 700,
                letterSpacing: -0.5,
                color: vendorBalanceColor(vendor.balance),
                ...NUMERIC,
              }}
            >
              {money(vendor.balance, currency)}
            </span>
            {vendor.limit ? (
              <span style={{ fontSize: 11.5, color: T.inkFaint, ...NUMERIC }}>
                {COPY.limit} {money(vendor.limit, currency)}
              </span>
            ) : null}
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={() => onPayment(vendor)}
              style={{
                width: 128,
                height: 44,
                background: T.shade,
                border: `1px solid ${T.border}`,
                borderRadius: T.radiusControl,
                font: 'inherit',
                fontSize: 15,
                fontWeight: 600,
                color: T.brand,
                cursor: 'pointer',
              }}
            >
              {COPY.payment}
            </button>
            <button
              type="button"
              onClick={() => onCharge(vendor)}
              style={{
                width: 128,
                height: 44,
                background: T.brand,
                border: 'none',
                borderRadius: T.radiusControl,
                font: 'inherit',
                fontSize: 15,
                fontWeight: 600,
                color: T.surface,
                cursor: 'pointer',
              }}
            >
              {COPY.charge}
            </button>
          </div>
        </div>
      </div>

      {/* ── the book ── */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          margin: '20px 28px',
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: T.radiusPanel,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: COLUMNS,
            gap: 16,
            padding: '12px 20px',
            background: T.shade,
            borderBottom: `1px solid ${T.border}`,
          }}
        >
          <span style={headCell}>{COPY.ledgerDescription}</span>
          <span style={headCell}>{COPY.ledgerDate}</span>
          <span style={{ ...headCell, textAlign: 'right' }}>{COPY.amount}</span>
          <span style={{ ...headCell, textAlign: 'right' }}>{COPY.ledgerBalance}</span>
          <span />
        </div>

        <div ref={bottomRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {loading ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 9,
                padding: 40,
                color: T.inkFaint,
              }}
            >
              <Spinner size={15} />
              <span style={{ fontSize: 13 }}>{COPY.loading}</span>
            </div>
          ) : transactions.length ? (
            transactions.map((transaction, index) => (
              <LedgerRow
                key={transaction._id || index}
                transaction={transaction}
                currency={currency}
                last={index === transactions.length - 1}
                onEdit={onEditTransaction}
                onDelete={onDeleteTransaction}
              />
            ))
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: 60,
                textAlign: 'center',
              }}
            >
              <span style={{ fontSize: 15, color: T.inkFaint }}>{COPY.emptyLedger}</span>
              <span style={{ fontSize: 12.5, color: T.inkGhost }}>{COPY.emptyLedgerBody}</span>
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            padding: '14px 20px',
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <span style={{ fontSize: 11.5, color: T.inkFaint }}>
            Newest entry is at the bottom; the balance runs downward.
          </span>

          {/* A tel: link, which is all a desktop can honestly offer. On a
              machine with no handler the click does nothing rather than
              pretending to dial. */}
          <a
            href={vendor.phone ? `tel:${vendor.phone}` : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              height: 36,
              padding: '0 14px',
              background: T.surface,
              border: `1px solid ${T.borderStrong}`,
              borderRadius: T.radiusControl,
              fontSize: 12.5,
              fontWeight: 600,
              color: vendor.phone ? T.inkSoft : T.inkGhost,
              textDecoration: 'none',
              cursor: vendor.phone ? 'pointer' : 'not-allowed',
            }}
          >
            <PhoneIcon />
            <span>{vendor.phone || COPY.phone}</span>
          </a>
        </div>
      </div>
    </div>
  );
}
